import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissions';
import { goalService } from '../services/goalService';
import GoalCard from '../components/goals/GoalCard';
import CreateGoalModal from '../components/goals/CreateGoalModal';
import GoalSettingsPanel from '../components/goals/GoalSettingsPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Notification from '../components/common/Notification';
import useNotification from '../hooks/useNotification';
import {
  Target,
  Plus,
  Filter,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Settings,
  RefreshCw,
} from 'lucide-react';

const Goals = () => {
  const { user } = useAuth();
  const isAdmin = hasAnyRole(user, ['Super Admin', 'Admin']);
  const { notification, showError, hideNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [goals, setGoals] = useState([]);
  const [error, setError] = useState(null);

  // Filters
  const [scopeFilter, setScopeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    achieved: 0,
    missed: 0,
    onTrack: 0,
    atRisk: 0,
  });

  const loadGoals = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (scopeFilter !== 'all') params.scope = scopeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (periodFilter !== 'all') params.period = periodFilter;

      const response = await goalService.getGoals(params);
      const goalsData = response.data;
      setGoals(goalsData);

      // Calculate stats
      const active = goalsData.filter(g => g.status === 'active');
      setStats({
        total: goalsData.length,
        active: active.length,
        achieved: goalsData.filter(g => g.status === 'achieved').length,
        missed: goalsData.filter(g => g.status === 'missed').length,
        onTrack: active.filter(g => isGoalOnTrack(g)).length,
        atRisk: active.filter(g => !isGoalOnTrack(g)).length,
      });
    } catch (err) {
      console.error('Error loading goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, statusFilter, periodFilter]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const isGoalOnTrack = (goal) => {
    const totalDays = (new Date(goal.period_end) - new Date(goal.period_start)) / (1000 * 60 * 60 * 24);
    const elapsedDays = (new Date() - new Date(goal.period_start)) / (1000 * 60 * 60 * 24);
    if (totalDays <= 0) return true;

    const expectedProgress = (elapsedDays / totalDays) * 100;

    if (goal.direction === 'minimize') {
      return Number(goal.current_value) <= Number(goal.target_value);
    }

    return Number(goal.progress_percentage) >= expectedProgress * 0.8;
  };

  const handleRefreshAll = async () => {
    if (!isAdmin) return;
    setRefreshing(true);

    try {
      await goalService.refreshAllGoals();
      await loadGoals();
    } catch (err) {
      console.error('Error refreshing goals:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteGoal = (goalId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Goal',
      message: 'Are you sure you want to delete this goal?',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await goalService.deleteGoal(goalId);
          setGoals(prev => prev.filter(g => g.id !== goalId));
        } catch (err) {
          console.error('Error deleting goal:', err);
          showError('Failed to delete goal');
        }
      },
    });
  };

  const handleEditGoal = (goal) => {
    setEditGoal(goal);
    setIsCreateModalOpen(true);
  };

  const handleGoalCreated = (goal) => {
    loadGoals();
    setEditGoal(null);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditGoal(null);
  };

  // Group goals by status
  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => ['achieved', 'missed', 'cancelled'].includes(g.status));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-white to-gray-50 p-6 rounded-2xl shadow-md border border-gray-100">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-900 via-primary-700 to-primary-500 bg-clip-text text-transparent flex items-center">
            <Target className="mr-3 h-10 w-10 text-yellow-600" />
            Goals
          </h1>
          <p className="text-gray-600 mt-2 font-medium">
            Track your progress and achieve your targets
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {isAdmin && (
            <button
              onClick={handleRefreshAll}
              disabled={refreshing}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh all goals"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
          >
            <Plus className="h-5 w-5" />
            <span>New Goal</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Goals"
          value={stats.total}
          icon={Target}
          color="gray"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          label="On Track"
          value={stats.onTrack}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="At Risk"
          value={stats.atRisk}
          icon={AlertTriangle}
          color="yellow"
        />
        <StatCard
          label="Achieved"
          value={stats.achieved}
          icon={CheckCircle}
          color="emerald"
        />
        <StatCard
          label="Missed"
          value={stats.missed}
          icon={XCircle}
          color="red"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
        <div className="flex items-center space-x-4 flex-wrap gap-y-2">
          <div className="flex items-center space-x-2 text-gray-600">
            <Filter className="h-5 w-5" />
            <span className="font-medium">Filters:</span>
          </div>

          {/* Scope Filter */}
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="user">Personal</option>
            <option value="firm">Firm</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="achieved">Achieved</option>
            <option value="missed">Missed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Period Filter */}
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="all">All Periods</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>

          {(scopeFilter !== 'all' || statusFilter !== 'all' || periodFilter !== 'all') && (
            <button
              onClick={() => {
                setScopeFilter('all');
                setStatusFilter('all');
                setPeriodFilter('all');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button
            onClick={loadGoals}
            className="mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
          >
            Try again
          </button>
        </div>
      ) : goals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-md border border-gray-100">
          <div className="mx-auto w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
            <Target className="h-10 w-10 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Goals Yet</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Goals help you track progress toward measurable targets.
            Create your first goal to get started.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Goal
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 text-primary-600 mr-2" />
                Active Goals ({activeGoals.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onEdit={handleEditGoal}
                    onDelete={handleDeleteGoal}
                    showActions={
                      goal.scope === 'user' ||
                      (goal.scope === 'firm' && isAdmin)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Completed Goals */}
          {completedGoals.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-600 mr-2" />
                Completed Goals ({completedGoals.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onEdit={handleEditGoal}
                    onDelete={handleDeleteGoal}
                    showActions={
                      goal.scope === 'user' ||
                      (goal.scope === 'firm' && isAdmin)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <CreateGoalModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleGoalCreated}
        editGoal={editGoal}
      />

      {/* Settings Panel */}
      <GoalSettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />

      <Notification {...notification} onClose={hideNotification} />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default Goals;
